"use client";

import { useTStream } from "#/hooks/use-t-stream";
import { useUserQuery } from "#/hooks/use-user";
import { atoms } from "#/lib/atoms/atoms";
import { logError, logger } from "#/lib/datadog";
import { toast } from "#/lib/toast";
import { Button } from "#/ui/button";
import { Card, CardContent, CardHeader } from "#/ui/card";
import { StreamLogo } from "#/ui/logo";
import { useAtomValue } from "jotai";
import { Copy, MessageCircle } from "lucide-react";
import { useCallback, useEffect, useMemo } from "react";
import { useIntercom } from "react-use-intercom";

const errorText = (errorDetails) => {
  const sections = [
    `Error Report`,
    `Error ID: ${errorDetails.errorId}`,
    `Time: ${errorDetails.timestamp}`,
    `Message: ${errorDetails.message}`,
    `URL: ${errorDetails.url}`,
    `Organization: ${errorDetails.orgId || "Unknown"}`,
    `User: ${errorDetails.userEmail}`,
    ``,
    `Stack Trace:`,
    errorDetails.stack,
    ``,
    `Browser: ${errorDetails.userAgent}`,
  ];

  return sections.join("\n");
};

export default function FallbackError(props: {
  error: Error;
  action?: {
    onClick: () => void;
    text: string;
  };
}) {
  const orgId = useAtomValue(atoms.orgId);
  const { data: user } = useUserQuery();
  const error = props?.error;
  const { t } = useTStream("common");
  const { showNewMessage, boot } = useIntercom();

  const errorId = useMemo(() => `${crypto.randomUUID()?.split("-").pop()}`, []);

  const errorDetails = useMemo(() => {
    return {
      errorId,
      message: error?.message || "Unknown error",
      stack:
        error?.stack?.split("\n").slice(0, 5).join("\n") ||
        "No stack trace available",
      url: typeof window !== "undefined" ? window.location.href : "Unknown",
      userAgent:
        typeof window !== "undefined" ? navigator.userAgent : "Unknown",
      timestamp: new Date().toISOString(),
      orgId,
      userEmail: user?.email || "Unknown",
    };
  }, [errorId, error, orgId, user?.email]);

  const copyErrorDetails = useCallback(() => {
    navigator.clipboard.writeText(errorText(errorDetails)).then(() => {
      toast.success(t("errorDetailsCopied"));
    });
  }, [errorDetails]);

  const shareWithSupport = useCallback(() => {
    boot();
    showNewMessage(errorText(errorDetails));
  }, [showNewMessage, errorDetails]);
  useEffect(() => {
    logError(error, {
      errorId,
      orgId,
      email: user?.email,
      componentStack: "FallbackError boundary",
    });

    logger.error("Application error caught by error boundary", {
      errorId,
      orgId,
      userEmail: user?.email,
      userId: user?._id,
      componentName: "FallbackError",
      url: window.location.href,
      userAgent: navigator.userAgent,
    });

    console.error({
      error,
      errorId,
      context: { orgId, userEmail: user?.email },
    });
  }, [errorId, orgId, user?.email, user?._id, error]);

  return (
    <div className="w-full h-[70dvh] flex items-center justify-center">
      <Card className="w-full p-5 relative bg-secondary rounded-xl max-w-md mx-auto">
        <CardHeader className="flex flex-col items-center space-y-2">
          <StreamLogo />
          <h2 className="text-xl pt-4 text-secondary-foreground font-bold">
            {t("somethingWentWrongTitle")}
          </h2>
          <p className="text-sm text-secondary-foreground leading-loose text-center md:text-base/relaxed dark:text-gray-400">
            {`We've been notified about this error and we'll fix it ASAP. Please try refreshing in the meantime.`}
          </p>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          <div className="flex flex-row justify-center gap-3">
            <Button
              onClick={() => window.location.reload()}
              className="h-10 flex-1 justify-center"
              variant="default"
            >
              <RefreshCwIcon className="mr-2 h-4 w-4" />
              {t("reload")}
            </Button>
            <Button
              className="h-10 flex-1 justify-center"
              onClick={() => window.open("/logout", "_self")}
              variant="outline"
            >
              <LogOutIcon className="mr-2 h-4 w-4" />
              {t("logout")}
            </Button>
          </div>

          <div className="flex flex-row justify-center gap-3">
            <Button
              onClick={copyErrorDetails}
              className="h-10 flex-1 justify-center"
              variant="secondary"
              size="sm"
            >
              <Copy className="mr-2 h-4 w-4" />
              {t("copyDetails")}
            </Button>
            <Button
              onClick={shareWithSupport}
              className="h-10 flex-1 justify-center"
              variant="secondary"
              size="sm"
            >
              <MessageCircle className="mr-2 h-4 w-4" />
              {t("contactSupport")}
            </Button>
          </div>
        </CardContent>
        <div className="bg-background rounded-bl-[5px] px-2 absolute text-xs top-0 right-0 text-center text-destructive font-bold py-1">
          ERROR: {errorId}
        </div>
      </Card>
    </div>
  );
}

function LogOutIcon(props) {
  return (
    <svg
      {...props}
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <polyline points="16 17 21 12 16 7" />
      <line x1="21" x2="9" y1="12" y2="12" />
    </svg>
  );
}

function RefreshCwIcon(props) {
  return (
    <svg
      {...props}
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8" />
      <path d="M21 3v5h-5" />
      <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16" />
      <path d="M8 16H3v5" />
    </svg>
  );
}
